/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from "@ember/object";
import Component from "@glimmer/component";
import formatToISO8601Date from "parog-web/utils/format-to-iso8601-date";
import type { DmpDocumentsSearchParams } from "parog-web/cnam/types";
import { DocumentStatus, DocumentVisibility } from "parog-web/cnam/types";
import type DmpConsultationContext from "parog-web/contexts/dmp-consultation-context";
import type DmpDocumentMetadata from "parog-web/models/dmp-document-metadata";
import dmpDocumentTypeCodes from "parog-web/utils/dmp-document-type-codes";

interface Args {
  context: DmpConsultationContext;
}

export default class DocumentsRetrieveAccessFormSearchComponent extends Component<Args> {
  constructor(owner: unknown, args: Args) {
    super(owner, args);
  }

  get title(): string {
    return `Recherche de documents DMP${
      this.args.context.secretConnection ? " - Connexion secrète en cours" : ""
    }`;
  }

  @action
  setLastDmpDocumentsSearchDate(value: Date) {
    this.args.context.medic.lastDmpDocumentsSearchDate = value;
    this.args.context.medic.save();
  }

  get dmpDocumentTypeCodes(): Array<unknown> {
    return dmpDocumentTypeCodes;
  }

  get lastSubmissionStartTimeFrom(): Date | undefined {
    const lastSearchDateString =
      this.args.context.medic.lastDmpDocumentsSearchDate;
    return lastSearchDateString ? new Date(lastSearchDateString) : undefined;
  }

  get serviceStartTimeFromDate(): Date | undefined {
    return this.args.context.searchParams.serviceStartTimeFrom
      ? new Date(this.args.context.searchParams.serviceStartTimeFrom)
      : undefined;
  }

  get serviceStopTimeToDate(): Date | undefined {
    return this.args.context.searchParams.serviceStopTimeTo
      ? new Date(this.args.context.searchParams.serviceStopTimeTo)
      : undefined;
  }

  get submissionStartTimeFromDate(): Date | undefined {
    return this.args.context.searchParams.submissionStartTimeFrom
      ? new Date(this.args.context.searchParams.submissionStartTimeFrom)
      : undefined;
  }

  getClassCodeDisplayName(classCode: string): string {
    // Loop through DMP
    const dmpClass = dmpDocumentTypeCodes.find(
      dmpClass => dmpClass.classCode == classCode
    );
    return dmpClass ? dmpClass.displayName : classCode;
  }

  getStatusString(statuses: Array<DocumentStatus>): string {
    const status: Array<string> = [];

    if (statuses.includes(DocumentStatus.DEPRECATED)) {
      status.push("ancienne version obsolète");
    }

    if (statuses.includes(DocumentStatus.ARCHIVED)) {
      status.push("archivé");
    }

    return status.join(", ");
  }

  getVisibilityString(confidentialities: Array<DocumentVisibility>): string {
    const visibility: Array<string> = [];

    if (confidentialities.includes(DocumentVisibility.HIDDEN_TO_PATIENT)) {
      visibility.push("non visible par le patient");
    }

    if (
      confidentialities.includes(
        DocumentVisibility.HIDDEN_TO_HEALTHCARE_PROFESSIONAL
      )
    ) {
      visibility.push("masqué aux professionnels de santé");
    }

    if (
      confidentialities.includes(
        DocumentVisibility.HIDDEN_TO_LEGAL_REPRESENTATIVES
      )
    ) {
      visibility.push("non visible par les représentants légaux");
    }

    return visibility.join(", ");
  }

  @action
  onChange<K extends keyof DmpDocumentsSearchParams>(
    key: K,
    value: DmpDocumentsSearchParams[K]
  ): void {
    let serializedValue;

    if (key == "serviceStartTimeFrom" || key == "serviceStopTimeTo") {
      serializedValue = value ? formatToISO8601Date(value as Date) : undefined;
    } else if (key == "submissionStartTimeFrom") {
      serializedValue = value ? (value as Date).toISOString() : undefined;
    }

    if (key == "typeCodes" && value) {
      serializedValue = (value as string).split(",");
    }

    serializedValue ||= value;

    this.args.context.searchParams[key] = serializedValue;
  }

  @action
  useLastSubmissionStartTimeFrom(): void {
    this.onChange("submissionStartTimeFrom", this.lastSubmissionStartTimeFrom);
  }

  get fromAndToDatesOnError() {
    return (
      this.serviceStartTimeFromDate &&
      this.serviceStopTimeToDate &&
      this.serviceStartTimeFromDate > this.serviceStopTimeToDate
    );
  }

  @action
  async setCurrentDocumentMetadata(
    documentMetadata: DmpDocumentMetadata
  ): Promise<void> {
    this.args.context.currentDocumentMetadata = documentMetadata;
    this.args.context.currentState =
      await this.args.context.calculateCurrentState();
  }

  @action
  onSubmit(event: Event): void {
    event.preventDefault();
    this.setLastDmpDocumentsSearchDate(new Date());
    this.args.context.transitionTo();
  }
}
